// src/encounter/lib/note-delta-extension.ts
//
// PROD-2961 (D1, option A) · the `cps-note-delta` extension on a QuestionnaireResponse ITEM: the Delta of
// a plain-text note line that holds pieces, beside the answer's `valueString` (note-line-delta.ts).
// QuestionBank stores item extensions verbatim and is url-agnostic (QuestionnaireResponseResource.cs,
// `QuestionnaireResponseExtension`, persisted in the response's FhirJson since QuestionBank #155), so no
// server change is involved. Every reader that does not know this url ignores it and reads the text.
import type { QRExtensionWire, QRItemWire, QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import { lineSegs } from './note-line-delta';

/** The extension's url. */
export const NOTE_DELTA_URL = 'https://scrips.com/fhir/StructureDefinition/cps-note-delta';

/** The extension for one line's Delta (wire ops JSON). */
export function noteDeltaExtension(deltaJson: string): QRExtensionWire {
  return { url: NOTE_DELTA_URL, valueString: deltaJson };
}

/** The Delta an item carries, if any. */
export function readNoteDelta(extension: readonly QRExtensionWire[] | null | undefined): string | undefined {
  const found = (extension ?? []).find((e) => e.url === NOTE_DELTA_URL && typeof e.valueString === 'string' && e.valueString);
  return found?.valueString ?? undefined;
}

/** Is `deltaJson` worth sending for an answer `value`? Only while it holds a piece AND still derives the
 *  value (the checksum rule): a stale Delta is dropped and the text wins. With `encounterId`, a chart
 *  quote stamped for ANOTHER visit (G5: every snapshot carries the patient and visit it was read for)
 *  never rides on this one (Codex P1). */
export function noteDeltaFits(deltaJson: string | undefined, value: unknown, encounterId?: string): deltaJson is string {
  const segs = lineSegs(deltaJson, value);
  if (!segs) return false;
  if (!encounterId) return true;
  return segs.every((s) => s.piece?.t !== 'chart' || !s.piece.snapshot?.encounterId || s.piece.snapshot.encounterId === encounterId);
}

/** Every stored line Delta of a saved response, by linkId (nested items included). */
export function noteDeltasFromResource(resource: Pick<QuestionnaireResponseResource, 'item'> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  const walk = (items: readonly QRItemWire[] | undefined) => {
    for (const it of items ?? []) {
      const delta = readNoteDelta(it.extension);
      if (delta) out[it.linkId] = delta;
      walk(it.item);
    }
  };
  walk(resource?.item);
  return out;
}
